import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contentAPI } from '@/utils/api';
import { Loader } from '@/components/ui/loader';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export const Services = () => {
  const { t, i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await contentAPI.getServices();
      setServices(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  // animate cards once when the component first loads (not on every filter click)
  useEffect(() => {
    if (!loading) {
      // kill any previous triggers to start fresh
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());

      // ensure cards have no leftover transforms/opacity
      gsap.set('.service-card', { clearProps: 'transform,opacity' });

      gsap.from('.service-card', {
        scrollTrigger: {
          trigger: '.services-grid',
          start: 'top 70%'
        },
        opacity: 0,
        y: 50,
        duration: 0.8,
        stagger: 0.15
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [loading]);

  const getLangContent = (content) => {
    return content?.[i18n.language] || content?.en || '';
  };

  const filteredServices = selectedCategory === 'all'
    ? services
    : services.filter(s => s.category === selectedCategory);

  // clear any gsap transforms whenever the list changes (due to filtering)
  useEffect(() => {
    gsap.set('.service-card', { clearProps: 'transform,opacity' });
  }, [filteredServices]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Loader className="text-secondary" text={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="bg-primary min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="services-hero"
        className="relative py-32 flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 28, 45, 0.85), rgba(11, 28, 45, 0.85)), url('https://images.unsplash.com/photo-1768302368425-48328f7fb90d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBza3lzY3JhcGVyJTIwYmx1ZSUyMGdvbGR8ZW58MHx8fHwxNzcyMjY5ODc4fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
            Our Services
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Comprehensive corporate recovery and NPA resolution solutions
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-[#111827] border-b border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div data-testid="category-filter" className="flex flex-wrap justify-center gap-4">
            <button
              data-testid="filter-all"
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-sm text-sm font-semibold uppercase tracking-wider transition-all ${
                selectedCategory === 'all'
                  ? 'bg-secondary text-primary'
                  : 'bg-transparent border border-white/20 text-gray-300 hover:border-secondary hover:text-secondary'
              }`}
            >
              All Services
            </button>
            <button
              data-testid="filter-corporate"
              onClick={() => setSelectedCategory('corporate_recovery')}
              className={`px-6 py-3 rounded-sm text-sm font-semibold uppercase tracking-wider transition-all ${
                selectedCategory === 'corporate_recovery'
                  ? 'bg-secondary text-primary'
                  : 'bg-transparent border border-white/20 text-gray-300 hover:border-secondary hover:text-secondary'
              }`}
            >
              Corporate Recovery
            </button>
            <button
              data-testid="filter-npa"
              onClick={() => setSelectedCategory('npa_resolution')}
              className={`px-6 py-3 rounded-sm text-sm font-semibold uppercase tracking-wider transition-all ${
                selectedCategory === 'npa_resolution'
                  ? 'bg-secondary text-primary'
                  : 'bg-transparent border border-white/20 text-gray-300 hover:border-secondary hover:text-secondary'
              }`}
            >
              NPA Resolution
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid" className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="services-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {filteredServices.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                data-testid={`service-card-${service.slug}`}
                className="block h-full"
              >
                <div className="service-card group flex flex-col justify-between bg-[#111827] border border-white/5 hover:border-secondary/50 p-8 rounded-sm card-hover h-full">
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary text-xs uppercase tracking-widest rounded-sm">
                      {service.category === 'corporate_recovery' ? 'Corporate Recovery' : 'NPA Resolution'}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-white mb-3 group-hover:text-secondary transition-colors">
                    {getLangContent(service.title)}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">
                    {getLangContent(service.description)}
                  </p>
                  <div className="flex items-center text-secondary text-sm font-medium group-hover:translate-x-2 transition-transform">
                    {t('common.read_more')}
                    <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
